import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {
  Box,
  Button,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {useQuery} from '@apollo/client';
import {useSubmitHandover} from '../../hooks/useSubmitHandover';
import {useReviewContext} from '../../contexts/ReviewContext';
import {usePrePrecursorGQL} from '../../components/PrecursorForm/usePrePrecursorGQL';
import {BusinessUnitEnum, GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {themeColors} from '../../theme/Theme';
import {useOVRecommends} from '../../hooks/useOVRecommends';
import Loading from '../../components/Loading/Loading';
import {GET_ALL_SENIOR_ADVISERS} from '../../features/bulkAdmin/bulkAdminQueries';
import {GetAllSeniorAdvisers} from '../../schema-types/GetAllSeniorAdvisers';
import {HandoverValidationMessage} from '../../components/FooterNavigation/HandoverValidationMessage';
import {useReviewDataValidation} from '../../hooks/useClientDataValidation';
import {KeyClientDataBanner} from '../../components/KeyClientDataBanner/KeyClientDataBanner';
import {useAccountContext} from '../../contexts/AccountContext';

type screens = 'actions' | 'send-for-sign-off';

export const HandoverJuniorAdviserModals: React.FC<{onClose: () => void}> = ({
  onClose,
}) => {
  const {reviewValidation: review} = useReviewContext();

  const {softSubmit} = useSubmitHandover();
  const [screen, setScreen] = useState<screens>('actions');

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {updateReview} = usePrePrecursorGQL();

  const handleSendForSignOffConfirm = async (id?: string) => {
    closeSnackbar();
    if (!review) {
      enqueueSnackbar('Review data is not available', {variant: 'error'});
      return;
    }

    const res = await updateReview({
      variables: {
        reviewId: review.id,
        sendForSignoff: true,
        padReviewerId: id,
      },
    });

    if (res.data?.updateReview?.ok) {
      enqueueSnackbar('The current review is open', {variant: 'success'});
    } else {
      enqueueSnackbar('An error occurred when re-opening the current review', {
        variant: 'error',
      });
    }

    await softSubmit();
    onClose();
  };

  return (
    <>
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[GlobalPermissionEnum.can_access_pad_junior_handover_modal_FE]}
      >
        {screen === 'actions' && <Actions onClose={onClose} setScreen={setScreen} />}
        {screen === 'send-for-sign-off' && (
          <HandoverJuniorAdviserSendForSignOff
            onClose={() => setScreen('actions')}
            onConfirm={(id) => handleSendForSignOffConfirm(id)}
          />
        )}
      </FeatureBoundary>
    </>
  );
};

const Actions: React.FC<{
  setScreen: React.Dispatch<React.SetStateAction<screens>>;
  onClose: () => void;
}> = ({setScreen, onClose}) => {
  const {softSubmit, fullSubmit} = useSubmitHandover();
  const {data: recsData, loading} = useOVRecommends();
  const {allValid} = useReviewContext();
  const {accountType} = useAccountContext();
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const canFullSubmit = allValid && !hasReviewDataIssues;
  const anyRequirements = (recsData?.ovrecommends?.length ?? 0) > 0;

  const isSFP = accountType === BusinessUnitEnum.SFP;

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Typography pb={3} variant="h5">
        Junior adviser PAD review options
      </Typography>

      {hasReviewDataIssues && (
        <KeyClientDataBanner issues={reviewDataIssues} isSFP={isSFP} />
      )}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            style={{marginRight: '2rem'}}
            onClick={() => softSubmit().then(onClose)}
          >
            Soft Submission
          </Button>
          <Box mt={1}>
            <Typography>
              Submit my handover notes without the requirements. I will come back and
              finalise these.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          {anyRequirements ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setScreen('send-for-sign-off')}
              >
                Send for sign off
              </Button>
              <Box mt={1}>
                <Typography>
                  Submit my handover notes with the requirements which are finalised.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                style={{marginRight: '2rem'}}
                disabled={!canFullSubmit}
                onClick={async () => {
                  await fullSubmit();
                }}
              >
                Full Submission
              </Button>
              {!allValid && <HandoverValidationMessage />}
            </>
          )}

          <Box bgcolor={themeColors.lightWarning} borderRadius="2px" padding={2} mt={3}>
            <Typography variant="body1">
              Please note once you have fully submitted the handover notes, you will no
              longer be able to edit the requirements.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export const HandoverJuniorAdviserSendForSignOff: React.FC<{
  onClose: () => void;
  onConfirm: (id?: string) => void;
}> = ({onClose, onConfirm}) => {
  const {data, loading} = useQuery<GetAllSeniorAdvisers>(GET_ALL_SENIOR_ADVISERS);
  const [adviserId, setAdviserId] = useState('');

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Typography pb={3} variant="h5">
        Send for sign off
      </Typography>
      <Box display="flex" flexDirection="column">
        <Typography mb={2} variant="h6">
          I have completed the handover notes and requirements and would like to request
          approval from a senior adviser.
        </Typography>
        <Select
          data-testid="adviser-select"
          onChange={(e: SelectChangeEvent) => {
            setAdviserId(e.target.value);
          }}
          value={adviserId}
        >
          {data?.seniorAdvisers?.map((adviser) => {
            return (
              <MenuItem key={adviser.id} value={adviser.id}>
                {adviser.name} ({adviser.email})
              </MenuItem>
            );
          })}
        </Select>
        <Typography mb={2} variant="h6">
          Are you sure you would like to submit? On submission, the review will be sent to
          a senior adviser for approval.
        </Typography>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(adviserId)}
            color="primary"
            variant="contained"
            disabled={adviserId === ''}
          >
            Confirm
          </Button>
        </DialogActions>
      </Box>
    </>
  );
};
